import { useTenant } from "@/providers/session";
import { useEffect, useRef, useState } from "react";

type ColumnFilter = {
  id: string;
  value: any;
};

const useTableFilters = (
  tableName: string,
  initialColumnFilters: ColumnFilter[] = [],
  disablePersistance = false,
) => {
  const { tenantId } = useTenant();
  const hasInitialized = useRef(false);
  const [loading, setLoading] = useState(true);
  const [columnFilters, setColumnFilters] = useState(initialColumnFilters);

  useEffect(() => {
    if (!tenantId || hasInitialized.current) return;

    const loadFromStorage = () => {
      try {
        const storageKey = `${tenantId}_${tableName}_filters`;
        const storedFilters = sessionStorage.getItem(storageKey);

        if (storedFilters) {
          const parsedFilters = JSON.parse(storedFilters);
          setColumnFilters(parsedFilters);
        }
      } catch (error) {
        console.error("Error loading filters from sessionStorage:", error);
      } finally {
        hasInitialized.current = true;
        setLoading(false);
      }
    };

    if (document.readyState === "complete") {
      loadFromStorage();
    } else {
      window.addEventListener("load", loadFromStorage, { once: true });
      return () => window.removeEventListener("load", loadFromStorage);
    }
  }, [tenantId, tableName]);

  useEffect(() => {
    if (!tenantId || !hasInitialized.current || disablePersistance) return;

    const storageKey = `${tenantId}_${tableName}_filters`;

    sessionStorage.setItem(storageKey, JSON.stringify(columnFilters));
  }, [columnFilters, tenantId, tableName, disablePersistance]);

  const onColumnFilterChange = (filterState: any) => {
    if (loading) return;
    setColumnFilters(filterState(columnFilters));
  };

  return {
    columnFilters,
    onColumnFilterChange,
    isLoading: loading,
  };
};

export default useTableFilters;
